import React from 'react';
import Latex from 'react-latex-next';
import 'katex/dist/katex.min.css';
import type { LegacyChatResult } from '@/types/legacyChat';
import { LATEX_DELIMITED_SOURCE, isLatexToken } from '@/utils/latex';
import { expandRefs, lookupRef } from '@/utils/chatUtils/chatDefaultUtils';
import { WATCH_TOKEN_SOURCE, parseWatchToken } from './watchToken';
import WatchAgainChip from './WatchAgainChip';
import McGrawHillCiteBadge from '../../../../mcgrawHill/McGrawHillCiteBadge'; // mcgraw-hill

// Matches inline markdown plus reference markers: [A1]/[Q1]/… content refs and
// [p4] document-page refs (a document anchor cites the page it drew from so the
// reader can click to jump there). A delimited LaTeX run comes first (ORA-628)
// so an equation is one token and its `*`, `_` and `[` are never read as
// markdown; `**…**` around an equation still bolds it, because the recursive
// pass sees the equation again inside.
// mcgraw-hill: `[[cite 3]]`, a numbered source badge McGraw Hill answers
// carry (mcgrawHillCitations.ts makes the token from the AI's markers).
const MCGRAW_HILL_CITE_TOKEN_SOURCE = '\\[\\[cite \\d{1,2}\\]\\]';
const MCGRAW_HILL_CITE_TOKEN_RE = /^\[\[cite \d{1,2}\]\]$/;
const INLINE_MARKDOWN_PATTERN = `(${LATEX_DELIMITED_SOURCE}|\`[^\`]+\`|\\*\\*[^*]+\\*\\*|\\*[^*\\n]+\\*|${MCGRAW_HILL_CITE_TOKEN_SOURCE}|\\[(?:(?:[AQFVDU]\\d+(?::\\d+)?[\\s,]*)+|p\\d+)\\])`;
// A course student's companion (Courses V1) also reads the `[[watch 0:38-0:57]]`
// token (ORA-1055), before the bracket refs, which would otherwise read its
// outer brackets. Everyone else's answers keep the pattern above, so the text
// renders as written.
const INLINE_MARKDOWN_WITH_WATCH_PATTERN = `(${LATEX_DELIMITED_SOURCE}|\`[^\`]+\`|\\*\\*[^*]+\\*\\*|\\*[^*\\n]+\\*|${WATCH_TOKEN_SOURCE}|${MCGRAW_HILL_CITE_TOKEN_SOURCE}|\\[(?:(?:[AQFVDU]\\d+(?::\\d+)?[\\s,]*)+|p\\d+)\\])`;

/** How the renderer reads an answer beyond plain markdown. */
export interface MarkdownRenderOptions {
  /**
   * Draw the companion's `[[watch …]]` token as a Watch again chip: a course
   * student's companion only (Courses V1). Off, the token is plain text.
   */
  watchTokens?: boolean;
}
const PAGE_CITATION_RE = /^p(\d+)$/i;

// The renderer emits plain-text slices as React string children, which React
// renders verbatim — so HTML entities the model emits (e.g. "&kappa;" for κ,
// "&lambda;" for λ) would show literally. Decode them to their characters via
// the browser's own entity table. A <textarea>'s content is RCDATA, so assigning
// innerHTML decodes entities without parsing or executing any markup. Inline
// code spans are intentionally left literal by their caller.
const decodeHtmlEntities = (value: string): string => {
  if (!value.includes('&') || typeof document === 'undefined') return value;

  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

const renderCitationRefs = (
  citationBody: string,
  data?: LegacyChatResult | null,
  onCitationClick?: (ref: string, item: Record<string, unknown> | null) => void,
  keyPrefix = 'cite'
) =>
  expandRefs(citationBody)
    .filter((ref) => ref.startsWith('A'))
    .map((ref, index) => {
      const item = lookupRef(ref, data);

      return (
        <button
          key={`${keyPrefix}-${ref}-${index}`}
          className="chat-ui_cite"
          onClick={
            onCitationClick
              ? () =>
                  onCitationClick(ref, item as Record<string, unknown> | null)
              : undefined
          }
          type="button"
          title={ref}
        >
          {ref}
        </button>
      );
    });

const renderInlineMarkdown = (
  value: string,
  data?: LegacyChatResult | null,
  onCitationClick?: (ref: string, item: Record<string, unknown> | null) => void,
  keyPrefix = 'inline',
  options: MarkdownRenderOptions = {}
): React.ReactNode[] => {
  const pieces: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  const inlineMarkdownRe = new RegExp(
    options.watchTokens
      ? INLINE_MARKDOWN_WITH_WATCH_PATTERN
      : INLINE_MARKDOWN_PATTERN,
    'g'
  );

  while ((match = inlineMarkdownRe.exec(value))) {
    if (match.index > lastIndex)
      pieces.push(decodeHtmlEntities(value.slice(lastIndex, match.index)));

    const token = match[0];

    if (isLatexToken(token)) {
      // An escaped dollar (`\\$5`) is prose, never the start of an equation.
      if (token.startsWith('$') && value[match.index - 1] === '\\') {
        pieces.push(decodeHtmlEntities(token));
      } else {
        // Rendered with the KaTeX build the article and explanation views
        // already ship; the source is left as-is when KaTeX cannot parse it.
        pieces.push(
          <Latex key={`${keyPrefix}-math-${match.index}`}>{token}</Latex>
        );
      }
    } else if (token.startsWith('`')) {
      pieces.push(
        <code key={`${keyPrefix}-code-${match.index}`}>
          {token.slice(1, -1)}
        </code>
      );
    } else if (token.startsWith('**')) {
      pieces.push(
        <strong key={`${keyPrefix}-strong-${match.index}`}>
          {renderInlineMarkdown(
            token.slice(2, -2),
            data,
            onCitationClick,
            `${keyPrefix}-strong-${match.index}`,
            options
          )}
        </strong>
      );
    } else if (token.startsWith('*')) {
      pieces.push(
        <em key={`${keyPrefix}-em-${match.index}`}>
          {renderInlineMarkdown(
            token.slice(1, -1),
            data,
            onCitationClick,
            `${keyPrefix}-em-${match.index}`,
            options
          )}
        </em>
      );
    } else if (MCGRAW_HILL_CITE_TOKEN_RE.test(token)) {
      // mcgraw-hill: a numbered source badge (mcgrawHillCitations.ts).
      pieces.push(
        <McGrawHillCiteBadge
          key={`${keyPrefix}-mh-cite-${match.index}`}
          number={Number(token.slice(7, -2))}
        />
      );
    } else if (options.watchTokens && token.startsWith('[[')) {
      const range = parseWatchToken(token);

      pieces.push(
        range ? (
          <WatchAgainChip
            key={`${keyPrefix}-watch-${match.index}`}
            range={range}
          />
        ) : (
          decodeHtmlEntities(token)
        )
      );
    } else if (token.startsWith('[')) {
      const citationBody = token.slice(1, -1);
      const pageMatch = PAGE_CITATION_RE.exec(citationBody);

      if (pageMatch) {
        // Document page citation → a pill that scrolls the viewer to the page.
        // Reuses the citation-click channel with a "p<N>" ref (handled upstream).
        const page = Number(pageMatch[1]);

        pieces.push(
          <button
            key={`${keyPrefix}-page-${match.index}`}
            className="chat-ui_cite"
            onClick={
              onCitationClick
                ? () => onCitationClick(`p${page}`, null)
                : undefined
            }
            type="button"
            aria-label={`Go to page ${page}`}
            title={`Go to page ${page}`}
          >
            p.{page}
          </button>
        );
      } else {
        pieces.push(
          ...renderCitationRefs(
            citationBody,
            data,
            onCitationClick,
            `${keyPrefix}-cite-${match.index}`
          )
        );
      }
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < value.length)
    pieces.push(decodeHtmlEntities(value.slice(lastIndex)));

  return pieces;
};

const renderHeading = (
  level: number,
  key: string,
  children: React.ReactNode
) => {
  if (level === 1) return <h1 key={key}>{children}</h1>;
  if (level === 2) return <h2 key={key}>{children}</h2>;
  if (level === 3) return <h3 key={key}>{children}</h3>;
  if (level === 4) return <h4 key={key}>{children}</h4>;
  if (level === 5) return <h5 key={key}>{children}</h5>;
  return <h6 key={key}>{children}</h6>;
};

// GFM table delimiter row, e.g. "| --- | :---: |" or "---|---". Requires a pipe
// (below) so a bare "---" horizontal rule / setext underline isn't mistaken for one.
const TABLE_DELIMITER_PATTERN =
  /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/;

const isDelimiterRow = (line: string | undefined): boolean =>
  !!line && line.includes('|') && TABLE_DELIMITER_PATTERN.test(line);

// A line that looks like a table row: has a pipe and isn't a heading/list item.
const isTableRowLine = (line: string | undefined): boolean =>
  !!line &&
  !!line.trim() &&
  line.includes('|') &&
  !line.match(/^(#{1,6})\s+/) &&
  !line.match(/^\s*[-*]\s+/) &&
  !line.match(/^\s*\d+\.\s+/);

// A table starts at a header row containing a pipe when it is followed by EITHER
// a standard GFM delimiter row, OR (leniently) another table row with the same
// multi-column shape. The lenient case rescues tables the model emits without
// the `| --- |` delimiter row — which would otherwise render as raw `|` pipes.
// The matching-column-count requirement keeps stray pipes in prose from being
// mistaken for a table.
const isTableStart = (lines: string[], i: number): boolean => {
  if (!isTableRowLine(lines[i])) return false;
  const next = lines[i + 1];
  if (isDelimiterRow(next)) return true;
  if (!isTableRowLine(next)) return false;
  const headerCellCount = splitTableRow(lines[i]).length;
  return headerCellCount >= 2 && headerCellCount === splitTableRow(next).length;
};

// Split a "| a | b |" row into trimmed cells, dropping the empty leading/trailing
// cells produced by optional outer pipes. (Literal escaped pipes inside a cell are
// not supported — rare in this content and not worth a lookbehind for older Safari.)
const splitTableRow = (line: string): string[] => {
  let trimmed = line.trim();
  if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
  if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1);
  return trimmed.split('|').map((cell) => cell.trim());
};

const ANSWER_OPTION_RE = /(^|\s)([A-E])[.)]\s+(?=[A-Z])/g;

/**
 * Lettered answer options are not markdown lists, so nothing stops the
 * paragraph builder joining them into one run-on line ("A. Decreased venous
 * compliance B. Increased systemic vascular resistance C. ..."). Splits a
 * paragraph into one line per option, or returns null to leave it alone.
 *
 * The run must be at least three markers, start at A or B, and step one letter
 * at a time. That sequence requirement is what keeps ordinary prose out —
 * "E. coli" and "C. difficile" can never form an ascending run from A.
 */
const splitAnswerOptionRun = (text: string): string[] | null => {
  const marks: Array<{ index: number; letter: string }> = [];
  const matcher = new RegExp(ANSWER_OPTION_RE.source, 'g');
  let match: RegExpExecArray | null;

  while ((match = matcher.exec(text))) {
    marks.push({ index: match.index + match[1].length, letter: match[2] });
  }

  if (marks.length < 3) return null;

  const first = marks[0].letter.charCodeAt(0);
  if (first > 'B'.charCodeAt(0)) return null;
  if (marks.some((mark, i) => mark.letter.charCodeAt(0) !== first + i)) {
    return null;
  }

  const pieces: string[] = [];
  const lead = text.slice(0, marks[0].index).trim();
  if (lead) pieces.push(lead);

  marks.forEach((mark, i) => {
    const end = i + 1 < marks.length ? marks[i + 1].index : text.length;
    pieces.push(text.slice(mark.index, end).trim());
  });

  return pieces.filter(Boolean);
};

/**
 * Ora's hand-rolled markdown renderer. Exported because the AI-companion thread
 * (ORA-447) renders assistant text through the same renderer — the spec ports
 * this renderer rather than replacing it, so it outlived the legacy panel. New
 * callers pass text only: `data` and `onCitationClick` exist for the legacy
 * panel's inline `[A1]`/`[pN]` markers, which the new protocol does not use.
 */
export const renderMarkdownBlocks = (
  text: string,
  data?: LegacyChatResult | null,
  onCitationClick?: (ref: string, item: Record<string, unknown> | null) => void,
  options: MarkdownRenderOptions = {}
) => {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      const level = Math.min(headingMatch[1].length, 6);

      blocks.push(
        renderHeading(
          level,
          `heading-${index}`,
          renderInlineMarkdown(
            headingMatch[2],
            data,
            onCitationClick,
            `heading-${index}`,
            options
          )
        )
      );

      index += 1;
      continue;
    }

    const unorderedMatch = line.match(/^\s*[-*]\s+(.+)$/);
    if (unorderedMatch) {
      // Keys come from where the block STARTS, never from where it ends
      // (ORA-460): a list or table that grows while the answer streams would
      // otherwise change key on every new line and remount, which throws away
      // the reader's text selection and scroll position.
      const blockStart = index;
      const items: React.ReactNode[] = [];

      while (index < lines.length) {
        const itemMatch = lines[index].match(/^\s*[-*]\s+(.+)$/);
        if (!itemMatch) break;

        items.push(
          <li key={`ul-${index}`}>
            {renderInlineMarkdown(
              itemMatch[1],
              data,
              onCitationClick,
              `ul-${index}`,
              options
            )}
          </li>
        );

        index += 1;
      }

      blocks.push(<ul key={`ul-${blockStart}`}>{items}</ul>);
      continue;
    }

    const orderedMatch = line.match(/^\s*\d+\.\s+(.+)$/);
    if (orderedMatch) {
      const blockStart = index;
      const items: React.ReactNode[] = [];

      while (index < lines.length) {
        const itemMatch = lines[index].match(/^\s*\d+\.\s+(.+)$/);
        if (!itemMatch) break;

        items.push(
          <li key={`ol-${index}`}>
            {renderInlineMarkdown(
              itemMatch[1],
              data,
              onCitationClick,
              `ol-${index}`,
              options
            )}
          </li>
        );

        index += 1;
      }

      blocks.push(<ol key={`ol-${blockStart}`}>{items}</ol>);
      continue;
    }

    if (isTableStart(lines, index)) {
      const blockStart = index;
      const headerCells = splitTableRow(lines[index]);
      index += 1; // consume the header row
      if (isDelimiterRow(lines[index])) index += 1; // delimiter row is optional

      const bodyRows: string[][] = [];
      while (
        index < lines.length &&
        isTableRowLine(lines[index]) &&
        !isDelimiterRow(lines[index])
      ) {
        bodyRows.push(splitTableRow(lines[index]));
        index += 1;
      }

      const tableKey = `table-${blockStart}`;

      blocks.push(
        <div className="chat-ui_table-wrap" key={tableKey}>
          <table className="chat-ui_table">
            <thead>
              <tr>
                {headerCells.map((cell, cellIndex) => (
                  <th key={`${tableKey}-th-${cellIndex}`}>
                    {renderInlineMarkdown(
                      cell,
                      data,
                      onCitationClick,
                      `${tableKey}-th-${cellIndex}`,
                      options
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, rowIndex) => (
                <tr key={`${tableKey}-tr-${rowIndex}`}>
                  {headerCells.map((_, cellIndex) => (
                    <td key={`${tableKey}-td-${rowIndex}-${cellIndex}`}>
                      {renderInlineMarkdown(
                        row[cellIndex] ?? '',
                        data,
                        onCitationClick,
                        `${tableKey}-td-${rowIndex}-${cellIndex}`,
                        options
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

      continue;
    }

    const blockStart = index;
    const paragraphLines = [line.trim()];
    index += 1;

    while (
      index < lines.length &&
      lines[index].trim() &&
      !lines[index].match(/^(#{1,6})\s+(.+)$/) &&
      !lines[index].match(/^\s*[-*]\s+(.+)$/) &&
      !lines[index].match(/^\s*\d+\.\s+(.+)$/) &&
      !isTableStart(lines, index)
    ) {
      paragraphLines.push(lines[index].trim());
      index += 1;
    }

    const paragraphText = paragraphLines.join(' ');
    const optionLines = splitAnswerOptionRun(paragraphText);

    blocks.push(
      <p key={`p-${blockStart}`}>
        {optionLines
          ? optionLines.map((optionLine, optionIndex) => (
              <React.Fragment key={`p-${blockStart}-opt-${optionIndex}`}>
                {optionIndex > 0 ? <br /> : null}
                {renderInlineMarkdown(
                  optionLine,
                  data,
                  onCitationClick,
                  `p-${blockStart}-opt-${optionIndex}`,
                  options
                )}
              </React.Fragment>
            ))
          : renderInlineMarkdown(
              paragraphText,
              data,
              onCitationClick,
              `p-${blockStart}`,
              options
            )}
      </p>
    );
  }

  return blocks;
};
